import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchGenderProducts } from '../../services';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import ProductCard from '../productCard';
import Spinner from '../spinner';
import PageCollectionHeader from '../pageCollectionHeader';
import { InfiniteLoader } from '../infiniteLoader/infiniteLoader';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import CatalogEmptyState from '../catalogEmptyState/CatalogEmptyState';
import './productGender.scss';

const TAKE = 12;

const genderLabels: Record<string, string> = {
  womens: "Women's Jewelry",
  mens: "Men's Jewelry",
  unisex: 'Unisex Jewelry',
};

const normalizeGender = (value?: string) => {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'women' || normalized === 'woman' || normalized === 'womens') {
    return 'womens';
  }

  if (normalized === 'men' || normalized === 'man' || normalized === 'mens') {
    return 'mens';
  }

  return normalized === 'unisex' ? 'unisex' : '';
};

const ProductGender = () => {
  const dispatch = useAppDispatch();
  const { gender } = useParams();
  const resolvedGender = normalizeGender(gender);
  const { status, products, currentType, hasMore, skip } = useAppSelector(
    (state) => state.productsType
  );
  const loading = status === 'loading';
  const collectionKey = `gender:${resolvedGender}`;
  const title = genderLabels[resolvedGender] || 'Jewelry';

  useScrollRestoration(!loading && products.length > 0, collectionKey || 'gender');

  const loadMore = useCallback(() => {
    if (!resolvedGender || loading || !hasMore) return;

    const nextSkip = currentType === collectionKey ? skip : 0;
    dispatch(fetchGenderProducts({ gender: resolvedGender, skip: nextSkip, take: TAKE }));
  }, [collectionKey, currentType, dispatch, hasMore, loading, resolvedGender, skip]);

  useEffect(() => {
    if (resolvedGender && (currentType !== collectionKey || products.length === 0)) {
      dispatch(fetchGenderProducts({ gender: resolvedGender, skip: 0, take: TAKE }));
    }
  }, [collectionKey, currentType, dispatch, products.length, resolvedGender]);

  return (
    <div className="gender-page-container">
      <PageCollectionHeader
        eyebrow="Collection"
        title={title}
        subtitle="Products filtered by gender"
      />
      {status === 'loading' && products.length === 0 ? (
        <Spinner />
      ) : status === 'failed' ? (
        <CatalogEmptyState
          role="alert"
          mark="!"
          eyebrow="Catalog unavailable"
          title={`We can’t load ${title} right now.`}
          message="The product service is not responding. Start the backend and refresh this page to load this collection."
          primaryAction={{
            label: 'Refresh collection',
            to: `/products/gender/${resolvedGender}`,
            reloadDocument: true,
          }}
          secondaryAction={{ label: 'Go home', to: '/' }}
        />
      ) : products.length === 0 ? (
        <CatalogEmptyState
          eyebrow="Collection is empty"
          title={`No pieces in ${title} yet.`}
          message="This collection is not stocked right now. Try the full catalog or browse by jewelry type while we update the selection."
          primaryAction={{ label: 'View all jewelry', to: '/products' }}
          secondaryAction={{ label: 'Shop rings', to: '/products/types/Rings' }}
        />
      ) : (
        <InfiniteLoader loadMore={loadMore} hasMore={hasMore} loading={loading}>
          <div className="card-container">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {loading && <Spinner />}
        </InfiniteLoader>
      )}
    </div>
  );
};

export default ProductGender;
